import type { OperatingHoursDayWrite, OperatingHoursInterval, OperatingHoursList } from '@apiTypes'
import { WeekdayEnum } from '@apiTypes'
import { isNullOrUndefined } from '@/utils/common.utils'

/** Monday=0 … Sunday=6 — matches backend `WeekdayChoices`. */
export const weekdaysOrdered: readonly WeekdayEnum[] = [
  WeekdayEnum.Value0,
  WeekdayEnum.Value1,
  WeekdayEnum.Value2,
  WeekdayEnum.Value3,
  WeekdayEnum.Value4,
  WeekdayEnum.Value5,
  WeekdayEnum.Value6,
] as const

/** Native empty time inputs open the picker on "now"; seed this instead. */
export const OPERATING_HOURS_PICKER_START = '00:00'

/** weekdaysOrdered runs Monday..Sunday, so the first five are the working week. */
const WORKDAYS_PER_WEEK = 5

const TIME_PATTERN = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/

export interface OperatingHoursDayRow {
  weekday: WeekdayEnum
  isOpen: boolean
  intervals: OperatingHoursInterval[]
}

export interface OperatingHoursFormInterval {
  key: number
  startTime: string
  endTime: string
}

export interface OperatingHoursFormDay {
  weekday: WeekdayEnum
  weekdayLabel: string
  isOpen: boolean
  intervals: OperatingHoursFormInterval[]
}

/**
 * Labels are literal `t('...')` calls so vue-i18n-extract can find them —
 * a lookup through a key map would be invisible to the extractor.
 * Values: Monday=0 … Sunday=6 (backend `WeekdayChoices`).
 */
export function weekdayLabel(weekday: WeekdayEnum, t: (key: string) => string): string {
  switch (weekday) {
    case WeekdayEnum.Value0:
      return t('Monday')
    case WeekdayEnum.Value1:
      return t('Tuesday')
    case WeekdayEnum.Value2:
      return t('Wednesday')
    case WeekdayEnum.Value3:
      return t('Thursday')
    case WeekdayEnum.Value4:
      return t('Friday')
    case WeekdayEnum.Value5:
      return t('Saturday')
    case WeekdayEnum.Value6:
      return t('Sunday')
  }
}

/**
 * API times arrive as `HH:mm:ss`. Figma shows `H:mm` (no leading zero, no seconds).
 */
export function formatOperatingHoursTime(time: string): string {
  const [hours = '0', minutes = '00'] = time.split(':')

  return `${Number(hours)}:${minutes}`
}

export function formatOperatingHoursIntervals(intervals: OperatingHoursInterval[]): string {
  return intervals
    .map(interval => `${formatOperatingHoursTime(interval.startTime)} – ${formatOperatingHoursTime(interval.endTime)}`)
    .join(', ')
}

/**
 * Always returns Monday–Sunday. Days missing from the API are treated as closed.
 */
export function buildOperatingHoursWeek(days: OperatingHoursList[]): OperatingHoursDayRow[] {
  const byWeekday = new Map(days.map(day => [day.weekday, day]))

  return weekdaysOrdered.map((weekday) => {
    const day = byWeekday.get(weekday)

    return {
      weekday,
      isOpen: day?.isOpen ?? false,
      intervals: day?.intervals ?? [],
    }
  })
}

/**
 * Local and unexported on purpose. These were shared constants until toggling a day back on
 * stopped guessing times for the user - but a first-time setup is the one place a guess is
 * wanted, so the wizard's opening week keeps them rather than starting on five blank days.
 */
const SETUP_WEEK_START = '07:00'
const SETUP_WEEK_END = '18:00'

/**
 * The week a first-time setup opens with: weekdays open at the default hours, weekend closed.
 * Distinct from `buildOperatingHoursWeek([])`, which closes every day - correct for a saved week,
 * wrong for a wizard, where it would make the user open five days before they could finish.
 */
export function buildDefaultOperatingHoursWeek(): OperatingHoursDayRow[] {
  return weekdaysOrdered.map((weekday, index) => {
    const isWorkday = index < WORKDAYS_PER_WEEK

    return {
      weekday,
      isOpen: isWorkday,
      intervals: isWorkday
        ? [{
          startTime: toApiTime(SETUP_WEEK_START),
          endTime: toApiTime(SETUP_WEEK_END),
        }]
        : [],
    }
  })
}

export function isOperatingHoursDayOpen(day: { isOpen: boolean; intervals: readonly unknown[] }): boolean {
  return day.isOpen && day.intervals.length > 0
}

/**
 * True when a previously open minute is closed by the next week.
 * A later start, an earlier end, a dropped interval, or a closed day all count.
 */
export function operatingHoursWereReduced(
  current: readonly { weekday: WeekdayEnum; isOpen: boolean; intervals: readonly { startTime: string; endTime: string }[] }[],
  next: readonly OperatingHoursDayWrite[],
): boolean {
  const nextByWeekday = new Map(next.map(day => [day.weekday, day]))

  return current.some((day) => {
    const upcoming = nextByWeekday.get(day.weekday)
    const before = coveredMinutes(day.isOpen, day.intervals)
    const after = coveredMinutes(upcoming?.isOpen ?? false, upcoming?.intervals ?? [])

    return intervalsWereReduced(before, after)
  })
}

/**
 * True when any previously open minute falls outside `after`.
 * `after` is walked as sorted coverage, so a gap, a later start, or an earlier end all count.
 */
function intervalsWereReduced(before: readonly [number, number][], after: readonly [number, number][]): boolean {
  const sorted = [...after].sort((left, right) => left[0] - right[0])

  return before.some(([start, end]) => {
    let cursor = start

    for (const [rangeStart, rangeEnd] of sorted) {
      if (rangeStart > cursor)
        break

      if (rangeEnd > cursor)
        cursor = rangeEnd

      if (cursor >= end)
        return false
    }

    return cursor < end
  })
}

function coveredMinutes(
  isOpen: boolean,
  intervals: readonly { startTime: string; endTime: string }[],
): [number, number][] {
  if (!isOpen)
    return []

  return intervals.flatMap((interval) => {
    const start = timeToMinutes(interval.startTime)
    const end = timeToMinutes(interval.endTime)

    if (isNullOrUndefined(start) || isNullOrUndefined(end) || end <= start)
      return []

    return [[start, end] as [number, number]]
  })
}

export function timeToMinutes(time: string | null | undefined): number | null {
  if (typeof time !== 'string')
    return null

  const match = TIME_PATTERN.exec(time.trim())

  if (isNullOrUndefined(match))
    return null

  const hours = Number(match[1])
  const minutes = Number(match[2])
  const seconds = isNullOrUndefined(match[3]) ? 0 : Number(match[3])

  if (hours > 23 || minutes > 59 || seconds > 59)
    return null

  return hours * 60 + minutes
}

export function toFormTime(time: string): string {
  const total = timeToMinutes(time)

  if (isNullOrUndefined(total))
    return ''

  return formatFormTime(Math.floor(total / 60), total % 60)
}

export function formatFormTime(hours: number, minutes: number): string {
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

export function toApiTime(time: string): string {
  const formTime = toFormTime(time)

  return formTime ? `${formTime}:00` : ''
}

export function createOperatingHoursInterval(
  key: number,
  startTime = '',
  endTime = '',
): OperatingHoursFormInterval {
  return { key, startTime, endTime }
}

export function sortOperatingHoursIntervals<T extends { startTime: string; endTime: string }>(
  intervals: T[],
): T[] {
  return [...intervals].sort((left, right) => {
    const leftStart = timeToMinutes(left.startTime)
    const rightStart = timeToMinutes(right.startTime)

    if (isNullOrUndefined(leftStart) && isNullOrUndefined(rightStart))
      return 0
    if (isNullOrUndefined(leftStart))
      return 1
    if (isNullOrUndefined(rightStart))
      return -1
    if (leftStart !== rightStart)
      return leftStart - rightStart

    return (timeToMinutes(left.endTime) ?? 0) - (timeToMinutes(right.endTime) ?? 0)
  })
}

export function intervalsOverlap(
  left: { startTime: string; endTime: string },
  right: { startTime: string; endTime: string },
): boolean {
  const leftStart = timeToMinutes(left.startTime)
  const leftEnd = timeToMinutes(left.endTime)
  const rightStart = timeToMinutes(right.startTime)
  const rightEnd = timeToMinutes(right.endTime)

  if (
    isNullOrUndefined(leftStart)
    || isNullOrUndefined(leftEnd)
    || isNullOrUndefined(rightStart)
    || isNullOrUndefined(rightEnd)
  )
    return false

  return leftStart < rightEnd && rightStart < leftEnd
}

/**
 * Marks empty, inverted, and overlapping intervals. Adjacent intervals that only meet
 * (07:00–11:30 and 11:30–18:00) are allowed; a zero-length window (11:30–11:30) is not.
 *
 * `ignoreBlank` skips rows with no times yet, so a newly added interval is not painted red
 * until the user types in it or Save runs the strict schema.
 *
 * `overlapOnly` skips the empty/inverted bucket so callers that already report those
 * with a distinct message can still reuse the pairwise overlap check.
 */
export function findInvalidIntervalIndexes(
  intervals: { startTime: string; endTime: string }[],
  options?: { ignoreBlank?: boolean; overlapOnly?: boolean },
): number[] {
  const ignoreBlank = options?.ignoreBlank ?? false
  const overlapOnly = options?.overlapOnly ?? false
  const invalid = new Set<number>()

  const ranges = intervals.map((interval, index) => {
    const start = timeToMinutes(interval.startTime)
    const end = timeToMinutes(interval.endTime)
    const complete = !isNullOrUndefined(start) && !isNullOrUndefined(end)
    const blank = !interval.startTime?.trim() && !interval.endTime?.trim()

    if (!overlapOnly && !(ignoreBlank && blank) && (!complete || start >= end))
      invalid.add(index)

    return { index, start, end, complete: complete && start < end }
  })

  for (let i = 0; i < ranges.length; i++) {
    for (let j = i + 1; j < ranges.length; j++) {
      const left = ranges[i]
      const right = ranges[j]

      if (isNullOrUndefined(left) || isNullOrUndefined(right) || !left.complete || !right.complete)
        continue

      const leftInterval = intervals[left.index]
      const rightInterval = intervals[right.index]

      if (isNullOrUndefined(leftInterval) || isNullOrUndefined(rightInterval))
        continue

      if (intervalsOverlap(leftInterval, rightInterval)) {
        invalid.add(left.index)
        invalid.add(right.index)
      }
    }
  }

  return [...invalid]
}

export function setOperatingHoursDayOpen(
  day: OperatingHoursFormDay,
  isOpen: boolean,
  nextKey: () => number,
): void {
  day.isOpen = isOpen

  if (!isOpen) {
    day.intervals = []

    return
  }

  if (day.intervals.length === 0)
    day.intervals.push(createOperatingHoursInterval(nextKey()))
}

export function toOperatingHoursFormDays(
  days: OperatingHoursDayRow[],
  t: (key: string) => string,
  nextKey: () => number,
): OperatingHoursFormDay[] {
  return days.map((day) => {
    const isOpen = isOperatingHoursDayOpen(day)

    return {
      weekday: day.weekday,
      weekdayLabel: weekdayLabel(day.weekday, t),
      isOpen,
      intervals: isOpen
        ? sortOperatingHoursIntervals(day.intervals.map(interval => createOperatingHoursInterval(
          nextKey(),
          toFormTime(interval.startTime),
          toFormTime(interval.endTime),
        )))
        : [],
    }
  })
}

export function toOperatingHoursWrite(days: OperatingHoursFormDay[]): OperatingHoursDayWrite[] {
  return days.map(day => ({
    weekday: day.weekday,
    isOpen: day.isOpen,
    intervals: day.isOpen
      ? day.intervals.map(interval => ({
        startTime: toApiTime(interval.startTime),
        endTime: toApiTime(interval.endTime),
      }))
      : [],
  }))
}
